// Date utility functions for consistent date handling across the application

/**
 * Get the first day of the current month
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getFirstDayOfCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

/**
 * Get the current date
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
};

/**
 * Get a date that's a specific number of days ago from today
 * @param {number} days - Number of days to go back
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
};

/**
 * Get the first day of the previous month
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getFirstDayOfPreviousMonth = () => {
    const now = new Date();
    const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return firstDayPrevMonth.toISOString().split('T')[0];
};

/**
 * Validate that fromDate is not after toDate
 * @param {string} fromDate - Start date in YYYY-MM-DD format
 * @param {string} toDate - End date in YYYY-MM-DD format
 * @returns {boolean} True if valid, false if invalid
 */
export const isValidDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return false;
    return new Date(fromDate) <= new Date(toDate);
};

/**
 * Get default date range (last 30 days to today)
 * @returns {object} Object with fromDate and toDate
 */
export const getDefaultDateRange = () => {
    return {
        fromDate: getDaysAgo(30),
        toDate: getCurrentDate()
    };
};

/**
 * Get current month date range (1st of current month to today)
 * @returns {object} Object with fromDate and toDate
 */
export const getCurrentMonthRange = () => {
    return {
        fromDate: getFirstDayOfCurrentMonth(),
        toDate: getCurrentDate()
    };
};

/**
 * Format date for display
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
};

/**
 * Validate and correct date range if needed
 * @param {string} fromDate - Start date
 * @param {string} toDate - End date
 * @returns {object} Corrected date range
 */
export const validateAndCorrectDateRange = (fromDate, toDate) => {
    const currentDate = getCurrentDate();
    
    // If no dates provided, use default range
    if (!fromDate || !toDate) {
        return getDefaultDateRange();
    }
    
    // If fromDate is after toDate, swap them
    if (new Date(fromDate) > new Date(toDate)) {
        return {
            fromDate: toDate,
            toDate: fromDate
        };
    }
    
    // If both dates are in the future, use current month range
    if (new Date(fromDate) > new Date(currentDate) && new Date(toDate) > new Date(currentDate)) {
        return getCurrentMonthRange();
    }
    
    // If toDate is in the future, set it to today
    if (new Date(toDate) > new Date(currentDate)) {
        return {
            fromDate,
            toDate: currentDate
        };
    }
    
    return { fromDate, toDate };
};